import React, { useState } from 'react';
import {
  Shuffle, Printer, Clock, Eye, X, Plus, Trash2, Edit3, Save,
  CheckCircle, AlertTriangle, MapPin, Building2, Settings2,
  Shield, ChevronDown, ChevronUp, Users, XCircle, Search, Loader2, UserPlus,
  Info, ArrowRight,
} from 'lucide-react';
import BrassageDetailsModal from './BrassageDetailsModal';
import { useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import { ExclusionAbsentsPanel } from './ExclusionAbsentsPanel';
import { useGetPolesQuery, useGetClassesQuery } from '../../services/polesOptionsApi';
import {
  useGetSitesQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  useDeleteSiteMutation,
  useGetSallesQuery,
  useCreateSalleMutation,
  useUpdateSalleMutation,
  useDeleteSalleMutation,
  useGenererBrassageMutation,
  useVerifierBrassageMutation,
  useLazyGetResultatsParSalleQuery,
  useLazyGetResultatsParClasseQuery,
  useGetHistoriqueBrassageQuery,
  useDeleteSessionBrassageMutation,
  useToggleVisibiliteBrassageMutation,
  useLazyCandidatsRattrapageQuery,
  useAutoDetecterTpMutation,
  useLazyRechercherEtudiantQuery,
  type RechercheEtudiantResultat,
  type SiteExamen,
  type SalleExamen,
  type SiteExamenCreate,
  type SalleExamenCreate,
  type ResultatsParSalle,
  type ResultatsParClasse,
  type BrassageStats,
  type BrassageAlerte,
  type VerificationReport,
  type SessionCouverture,
  type EtudiantNonPlace,
  type SalleDisponible,
  useLazyGetEtudiantsNonPlacesQuery,
  useGetEtudiantsNonPlacesQuery,
  useGetSallesDisponiblesQuery,
  useAjouterEtudiantBrassageMutation,
} from '../../services/brassageApi';
import BrassageListePreview, {
  buildBrassageListeHTML,
  buildGroupsParSalle,
  buildGroupsParClasse,
  type BrassagePrintOptions,
} from '../../components/Brassage/BrassageListePreview';
import {
  buildListePresenceHTML,
  type PresenceSalleData,
  type PresenceMatiereInfo,
  type PresencePrintOptions,
} from '../../components/Brassage/ListePresenceBuilder';
import DeplacerEtudiantsModal from '../../components/Brassage/DeplacerEtudiantsModal';
import type { PlacementEtudiant } from '../../services/brassageApi';
import {
  useGetSessionsQuery as useGetEmploiSessionsQuery,
  useGetCreneauxQuery,
} from '../../services/emploisTempsApi';
import { useGetInfoInstitutQuery } from '../../services/coreApi';

// ─── Types ──────────────────────────────────────────────────────────────────

type ActiveTab = 'configuration' | 'generation' | 'impression' | 'recherche' | 'ajout_tardif' | 'historique';
type PrintMode = 'par_salle' | 'par_classe' | 'liste_presence';

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

// ─── Panel étudiants non placés ─────────────────────────────────────────────

interface EtudiantsNonPlacesPanelProps {
  sessionId: number;
  placementEnCours: string | null;
  setPlacementEnCours: (v: string | null) => void;
  placementSalleId: number | null;
  setPlacementSalleId: (v: number | null) => void;
}

const EtudiantsNonPlacesPanel: React.FC<EtudiantsNonPlacesPanelProps> = ({
  sessionId,
  placementEnCours,
  setPlacementEnCours,
  placementSalleId,
  setPlacementSalleId,
}) => {
  const { data: nonPlacesData, isLoading: loadingNonPlaces, refetch: refetchNonPlaces } = useGetEtudiantsNonPlacesQuery(
    { sessionId, all: true },
  );
  const { data: sallesDisponibles, refetch: refetchSalles } = useGetSallesDisponiblesQuery({ sessionId });
  const [ajouterEtudiant, { isLoading: placingStudent }] = useAjouterEtudiantBrassageMutation();
  const [placementMsg, setPlacementMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filterClasse, setFilterClasse] = useState<string>('');

  const etudiants = nonPlacesData?.resultats ?? [];
  const allClasses = etudiants
    .map(e => e.classe)
    .filter((c, i, arr) => c && arr.indexOf(c) === i)
    .sort();

  const filteredEtudiants = filterClasse
    ? etudiants.filter(e => e.classe === filterClasse)
    : etudiants;

  const sallesAvecPlace = (sallesDisponibles ?? []).filter(s => s.places_restantes > 0);
  const meilleureSalle = sallesAvecPlace.length > 0 ? sallesAvecPlace[0] : null;

  const handlePlacer = async (matricule: string) => {
    // placementSalleId === -1 sentinelle = mode "auto" (backend équilibre)
    const isAuto = placementSalleId === -1;
    const salleId = isAuto ? null : (placementSalleId || meilleureSalle?.id);
    if (!isAuto && !salleId) return;
    setPlacementMsg(null);
    try {
      const body = isAuto
        ? { sessionId, matricule, auto: true }
        : { sessionId, matricule, salle_id: salleId as number };
      const res = await ajouterEtudiant(body).unwrap();
      setPlacementMsg({ type: 'success', text: res.message });
      setPlacementEnCours(null);
      setPlacementSalleId(null);
      refetchNonPlaces();
      refetchSalles();
    } catch (err: any) {
      const msg = err?.data?.error || err?.data?.message || 'Erreur lors du placement';
      setPlacementMsg({ type: 'error', text: msg });
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-red-100 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
            <Users size={16} />
            Étudiants non placés ({etudiants.length})
          </h4>
          <div className="flex items-center gap-2">
            <select
              value={filterClasse}
              onChange={e => setFilterClasse(e.target.value)}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Toutes les classes ({etudiants.length})</option>
              {allClasses.map(c => (
                <option key={c} value={c}>{c} ({etudiants.filter(e => e.classe === c).length})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {placementMsg && (
        <div className={`px-4 py-2 text-sm ${placementMsg.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
          {placementMsg.type === 'success' ? <CheckCircle size={14} className="inline mr-1" /> : <XCircle size={14} className="inline mr-1" />}
          {placementMsg.text}
        </div>
      )}

      {loadingNonPlaces ? (
        <div className="p-6 text-center text-gray-500">
          <Loader2 size={20} className="animate-spin inline mr-2" />
          Chargement...
        </div>
      ) : etudiants.length === 0 ? (
        <div className="p-6 text-center text-green-600">
          <CheckCircle size={20} className="inline mr-2" />
          Tous les étudiants sont placés !
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">N°</th>
                <th className="text-left px-3 py-2">Matricule</th>
                <th className="text-left px-3 py-2">Nom</th>
                <th className="text-left px-3 py-2">Prénoms</th>
                <th className="text-left px-3 py-2">Classe</th>
                <th className="text-left px-3 py-2">Niveau</th>
                <th className="text-left px-3 py-2">Pôle</th>
                <th className="text-center px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredEtudiants.map((e, idx) => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                  <td className="px-3 py-1.5 text-gray-500">{idx + 1}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-600 dark:text-gray-400">{e.matricule}</td>
                  <td className="px-3 py-1.5 font-semibold text-gray-900 dark:text-gray-100">{e.nom}</td>
                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{e.prenoms}</td>
                  <td className="px-3 py-1.5">
                    <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-xs font-medium">{e.classe}</span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-500">{e.niveau}</td>
                  <td className="px-3 py-1.5 text-gray-500 truncate max-w-[100px]">{e.pole}</td>
                  <td className="px-3 py-1.5 text-center">
                    {placementEnCours === e.matricule ? (
                      <div className="flex items-center gap-1 justify-center">
                        <select
                          value={placementSalleId ?? meilleureSalle?.id ?? ''}
                          onChange={ev => setPlacementSalleId(ev.target.value ? Number(ev.target.value) : null)}
                          className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 max-w-[200px]"
                        >
                          {/* Option auto : le backend équilibre selon le mode de la session */}
                          <option value="-1">🎯 Auto (équilibrer)</option>
                          {sallesAvecPlace.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.site_nom} / {s.nom} ({s.places_restantes} place{s.places_restantes > 1 ? 's' : ''})
                            </option>
                          ))}
                          {sallesAvecPlace.length === 0 && <option value="">Aucune salle disponible</option>}
                        </select>
                        <button
                          onClick={() => handlePlacer(e.matricule)}
                          disabled={placingStudent || (placementSalleId !== -1 && sallesAvecPlace.length === 0)}
                          className="px-2 py-0.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {placingStudent ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle size={10} />}
                          OK
                        </button>
                        <button
                          onClick={() => { setPlacementEnCours(null); setPlacementSalleId(null); }}
                          className="px-1 py-0.5 text-gray-500 hover:text-red-500"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setPlacementEnCours(e.matricule); setPlacementSalleId(meilleureSalle?.id ?? null); setPlacementMsg(null); }}
                        className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-100 dark:hover:bg-blue-900/30 flex items-center gap-1 mx-auto"
                      >
                        <MapPin size={10} />
                        Placer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Résumé salles disponibles */}
      {sallesAvecPlace.length > 0 && (
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
          <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Salles avec places disponibles :</h5>
          <div className="flex flex-wrap gap-2">
            {sallesAvecPlace.slice(0, 10).map(s => (
              <span key={s.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                s.places_restantes > 10 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                : s.places_restantes > 3 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
              }`}>
                <Building2 size={10} />
                {s.site_nom}/{s.nom}: {s.places_restantes}/{s.capacite}
              </span>
            ))}
            {sallesAvecPlace.length > 10 && (
              <span className="text-xs text-gray-500">+{sallesAvecPlace.length - 10} autres</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Bloc : Déplacer un ou plusieurs étudiants (post-brassage) ──────────────
// Recherche par nom/matricule → coche des étudiants → modal DeplacerEtudiantsModal.
// Autonomie : gère sa recherche, sa sélection, et l'ouverture du modal.

interface DeplacementBlocProps { sessionId: number; }

const DeplacementBloc: React.FC<DeplacementBlocProps> = ({ sessionId }) => {
  const [rechercher, { data: rechData, isFetching }] = useLazyRechercherEtudiantQuery();
  const [q, setQ] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const results = rechData?.resultats ?? [];

  const toggle = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === results.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(results.map(r => r.id)));
  };

  // Convertit un RechercheEtudiantResultat en PlacementEtudiant partiel
  // (structure minimale attendue par le modal).
  const buildPlacementFromResult = (r: typeof results[number]): PlacementEtudiant => ({
    id: r.id,
    etudiant: 0,
    etudiant_nom: r.nom,
    etudiant_prenoms: r.prenoms,
    etudiant_sexe: '',
    etudiant_date_naissance: null,
    etudiant_lieu_naissance: '',
    etudiant_classe: r.classe,
    etudiant_pole: r.pole,
    etudiant_niveau: r.niveau,
    salle: r.salle_id,
    site_nom: r.site,
    salle_nom: r.salle,
  });

  const selected = results.filter(r => selectedIds.has(r.id));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-indigo-200 dark:border-indigo-800 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
        <ArrowRight size={20} className="text-indigo-500" />
        Déplacer un ou plusieurs étudiants vers une autre salle
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Recherchez les étudiants déjà placés (par nom, prénoms, matricule ou classe), cochez-les, puis choisissez la salle destination.
      </p>

      {/* Recherche */}
      <form
        onSubmit={e => {
          e.preventDefault();
          if (q.trim().length >= 2) {
            rechercher({ q: q.trim(), session_id: sessionId });
            setSelectedIds(new Set());
            setSuccessMsg('');
          }
        }}
        className="flex gap-3 mb-3"
      >
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Nom, prénoms, matricule ou classe (min. 2 caractères)…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <button
          type="submit"
          disabled={q.trim().length < 2 || isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {isFetching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Chercher
        </button>
      </form>

      {successMsg && (
        <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-green-700 dark:text-green-300 text-sm flex items-center gap-2">
          <CheckCircle size={14} /> {successMsg}
        </div>
      )}

      {rechData && results.length === 0 && (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
          Aucun étudiant trouvé dans cette session pour « {q} ».
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {results.length} résultat{results.length > 1 ? 's' : ''} — {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </div>
            <button
              onClick={() => setModalOpen(true)}
              disabled={selectedIds.size === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium disabled:opacity-40"
            >
              <ArrowRight size={14} /> Déplacer la sélection ({selectedIds.size})
            </button>
          </div>

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="w-8 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === results.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">Matricule</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">Nom & Prénoms</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">Classe</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">Salle actuelle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {results.map(r => (
                  <tr key={r.id} className={`hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 ${
                    selectedIds.has(r.id) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  }`}>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggle(r.id)} />
                    </td>
                    <td className="px-3 py-1.5 font-mono text-gray-700 dark:text-gray-300">{r.matricule}</td>
                    <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-white">{r.nom} {r.prenoms}</td>
                    <td className="px-3 py-1.5">
                      <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-xs">{r.classe}</span>
                    </td>
                    <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{r.site} / <span className="font-medium">{r.salle}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal de déplacement */}
      {modalOpen && selected.length > 0 && (
        <DeplacerEtudiantsModal
          sessionId={sessionId}
          placements={selected.map(buildPlacementFromResult)}
          onClose={() => setModalOpen(false)}
          onSuccess={nb => {
            setSuccessMsg(`${nb} étudiant(s) déplacé(s) avec succès.`);
            // Relance la recherche pour rafraîchir les salles actuelles
            if (q.trim().length >= 2) rechercher({ q: q.trim(), session_id: sessionId });
            setSelectedIds(new Set());
          }}
        />
      )}
    </div>
  );
};

// ─── Composant principal ────────────────────────────────────────────────────

const Brassage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('configuration');

  // ── Configuration state ──
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteExamen | null>(null);
  const [siteForm, setSiteForm] = useState<SiteExamenCreate>({ nom: '', adresse: '', ordre_affichage: 0, actif: true });
  const [showSalleForm, setShowSalleForm] = useState(false);
  const [editingSalle, setEditingSalle] = useState<SalleExamen | null>(null);
  const [salleForm, setSalleForm] = useState<SalleExamenCreate>({ site: 0, nom: '', capacite: 30, actif: true });
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'site' | 'salle'; id: number; nom: string } | null>(null);

  // ── Génération state ──
  const [anneeAcademique, setAnneeAcademique] = useState('2024-2025');
  const [mode, setMode] = useState<'par_pole' | 'par_niveau' | 'par_classe'>('par_niveau');
  const [selectedNiveaux, setSelectedNiveaux] = useState<string[]>([]);
  const [selectedPoles, setSelectedPoles] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<number[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [tauxRemplissage, setTauxRemplissage] = useState(100);
  const [modeRattrapage, setModeRattrapage] = useState(false);
  // 2026-09-15 : 2 = règle des semestres pairs (ajournés participants, ou non participants avec 4 mois réglés)
  const [semestreRattrapage, setSemestreRattrapage] = useState<1 | 2>(2);
  const [description, setDescription] = useState('');
  const [lastSessionId, setLastSessionId] = useState<number | null>(null);
  const [genResult, setGenResult] = useState<string>('');
  const [genStats, setGenStats] = useState<BrassageStats | null>(null);
  const [genCompactage, setGenCompactage] = useState<any | null>(null);
  const [showStatsDetail, setShowStatsDetail] = useState(false);
  const [verificationReport, setVerificationReport] = useState<VerificationReport | null>(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [showNonPlaces, setShowNonPlaces] = useState(false);
  const [placementEnCours, setPlacementEnCours] = useState<string | null>(null); // matricule
  const [placementSalleId, setPlacementSalleId] = useState<number | null>(null);

  // ── Impression state ──
  const [printSessionId, setPrintSessionId] = useState<number | null>(null);
  // Modal détail complet (sites, salles, capacités, description éditable)
  const [detailsSessionId, setDetailsSessionId] = useState<number | null>(null);
  const [printMode, setPrintMode] = useState<PrintMode>('par_salle');
  const [printFormat, setPrintFormat] = useState<'A4' | 'A3'>('A4');
  const [printOrientation, setPrintOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [printFont, setPrintFont] = useState("'Arial', Helvetica, sans-serif");
  const [printFontScale, setPrintFontScale] = useState(100);
  const [previewHTML, setPreviewHTML] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  // ── Listes de présence state ──
  const [presenceEmploiSessionId, setPresenceEmploiSessionId] = useState<number | null>(null);
  const [presenceSelectedDate, setPresenceSelectedDate] = useState<string>('');
  const [presenceSelectedHeure, setPresenceSelectedHeure] = useState<string>('');
  const [presenceSessionLabel, setPresenceSessionLabel] = useState('');
  const [presenceAnnee, setPresenceAnnee] = useState('');
  const [presenceSiteFilter, setPresenceSiteFilter] = useState<string>('');

  // ── Historique state ──
  const [histAnnee, setHistAnnee] = useState('');
  const [histDateDebut, setHistDateDebut] = useState('');
  const [histDateFin, setHistDateFin] = useState('');
  const [histNonPlacesSessionId, setHistNonPlacesSessionId] = useState<number | null>(null);

  // ── Common state ──
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  // Infos identité institut (ville + chef scolarité) pour pied de page impressions
  const { data: infoInstitut } = useGetInfoInstitutQuery();
  const [success, setSuccess] = useState('');

  // ── API hooks ──
  const { data: annees = [] } = useGetAnneesAcademiquesQuery({});
  const { data: poles = [] } = useGetPolesQuery({});
  const { data: classes = [] } = useGetClassesQuery({});
  const { data: sites = [] } = useGetSitesQuery();
  const { data: salles = [] } = useGetSallesQuery(
    selectedSiteId ? { site: selectedSiteId } : undefined
  );
  const [createSite] = useCreateSiteMutation();
  const [updateSite] = useUpdateSiteMutation();
  const [deleteSite] = useDeleteSiteMutation();
  const [createSalle] = useCreateSalleMutation();
  const [updateSalle] = useUpdateSalleMutation();
  const [deleteSalle] = useDeleteSalleMutation();
  const [genererBrassage] = useGenererBrassageMutation();
  const [triggerCandidats, { data: candidatsRattrapage, isFetching: loadingCandidats }] = useLazyCandidatsRattrapageQuery();
  const [autoDetecterTp, { isLoading: detectingTp }] = useAutoDetecterTpMutation();
  const [verifierBrassage] = useVerifierBrassageMutation();
  const [deleteSession] = useDeleteSessionBrassageMutation();
  const [toggleVisibiliteBrassage] = useToggleVisibiliteBrassageMutation();

  const handleToggleVisibiliteBrassage = async (s: { id: number; description: string; visible_aux_etudiants?: boolean }) => {
    const willBeVisible = !s.visible_aux_etudiants;
    if (!window.confirm(
      willBeVisible
        ? `Rendre la liste brassée « ${s.description || `#${s.id}`} » visible aux étudiants ?\n\nSeuls les étudiants placés dans cette liste la verront : leur salle et la recherche dans cette liste.`
        : `Masquer cette liste brassée des étudiants ?`
    )) return;
    try {
      await toggleVisibiliteBrassage({ brassageId: s.id, visible: willBeVisible }).unwrap();
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };
  const [fetchResultatsParSalle] = useLazyGetResultatsParSalleQuery();
  const [fetchResultatsParClasse] = useLazyGetResultatsParClasseQuery();
  const [rechercherEtudiant, { data: rechercheData, isFetching: rechercheLoading }] = useLazyRechercherEtudiantQuery();
  const [rechercheQuery, setRechercheQuery] = useState('');
  const [rechercheSessionId, setRechercheSessionId] = useState<number | null>(null);

  // ── Ajout tardif hooks ──
  const [fetchNonPlaces, { data: nonPlacesData, isFetching: nonPlacesLoading }] = useLazyGetEtudiantsNonPlacesQuery();
  const [ajouterEtudiant] = useAjouterEtudiantBrassageMutation();
  const [ajoutSessionId, setAjoutSessionId] = useState<number | null>(null);
  const [ajoutQuery, setAjoutQuery] = useState('');
  const [ajoutSalleId, setAjoutSalleId] = useState<number | null>(null);
  const [ajoutSuccess, setAjoutSuccess] = useState('');
  const [ajoutErreur, setAjoutErreur] = useState('');
  const { data: allSalles = [] } = useGetSallesQuery(undefined);
  const { data: historique } = useGetHistoriqueBrassageQuery({
    annee_academique: histAnnee || undefined,
    date_debut: histDateDebut || undefined,
    date_fin: histDateFin || undefined,
  });
  // Liste complète des sessions (sans filtres) pour la recherche
  const { data: allSessions } = useGetHistoriqueBrassageQuery({});

  // ── Emploi du temps hooks (pour listes de présence) ──
  const { data: emploiSessions = [] } = useGetEmploiSessionsQuery({});
  const { data: emploiCreneaux = [] } = useGetCreneauxQuery(
    presenceEmploiSessionId ? { session: presenceEmploiSessionId } : { session: -1 },
    { skip: !presenceEmploiSessionId }
  );

  // ── Site CRUD ──
  const openSiteForm = (site?: SiteExamen) => {
    if (site) {
      setEditingSite(site);
      setSiteForm({ nom: site.nom, adresse: site.adresse, ordre_affichage: site.ordre_affichage, actif: site.actif });
    } else {
      setEditingSite(null);
      setSiteForm({ nom: '', adresse: '', ordre_affichage: sites.length + 1, actif: true });
    }
    setShowSiteForm(true);
  };

  const handleSaveSite = async () => {
    if (!siteForm.nom.trim()) { setErreur('Le nom du site est requis.'); return; }
    try {
      if (editingSite) {
        await updateSite({ id: editingSite.id, data: siteForm }).unwrap();
      } else {
        await createSite(siteForm).unwrap();
      }
      setShowSiteForm(false);
    } catch { setErreur('Erreur lors de la sauvegarde du site.'); }
  };

  // ── Salle CRUD ──
  const openSalleForm = (salle?: SalleExamen) => {
    if (salle) {
      setEditingSalle(salle);
      setSalleForm({ site: salle.site, nom: salle.nom, capacite: salle.capacite, actif: salle.actif });
    } else {
      setEditingSalle(null);
      setSalleForm({ site: selectedSiteId ?? 0, nom: '', capacite: 30, actif: true });
    }
    setShowSalleForm(true);
  };

  const handleSaveSalle = async () => {
    if (!salleForm.nom.trim()) { setErreur('Le nom de la salle est requis.'); return; }
    if (salleForm.capacite <= 0) { setErreur('La capacité doit être supérieure à 0.'); return; }
    try {
      if (editingSalle) {
        await updateSalle({ id: editingSalle.id, data: salleForm }).unwrap();
      } else {
        await createSalle(salleForm).unwrap();
      }
      setShowSalleForm(false);
    } catch { setErreur('Erreur lors de la sauvegarde de la salle.'); }
  };

  // ── Delete handler ──
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'site') {
        await deleteSite(deleteConfirm.id).unwrap();
        if (selectedSiteId === deleteConfirm.id) setSelectedSiteId(null);
      } else {
        await deleteSalle(deleteConfirm.id).unwrap();
      }
      setDeleteConfirm(null);
    } catch { setErreur('Erreur lors de la suppression.'); }
  };

  // ── Génération ──
  const handleGenerer = async () => {
    setErreur(''); setSuccess(''); setGenResult(''); setGenStats(null); setGenCompactage(null);
    setShowStatsDetail(false); setVerificationReport(null); setShowVerification(false);
    setLoading(true);
    try {
      const result = await genererBrassage({
        annee_academique: anneeAcademique,
        mode,
        description: modeRattrapage ? (description || `Rattrapage ${anneeAcademique}`) : description,
        niveaux: selectedNiveaux.length > 0 ? selectedNiveaux : undefined,
        poles: selectedPoles.length > 0 ? selectedPoles : undefined,
        sites: selectedSites.length > 0 ? selectedSites : undefined,
        classes: selectedClasses.length > 0 ? selectedClasses : undefined,
        taux_remplissage: tauxRemplissage < 100 ? tauxRemplissage : undefined,
        seulement_a_rattraper: modeRattrapage || undefined,
        semestre: modeRattrapage ? semestreRattrapage : undefined,
      }).unwrap();
      setLastSessionId(result.session_id);
      setPrintSessionId(result.session_id);
      setGenResult(result.message);
      setGenStats(result.stats || null);
      setGenCompactage(result.compactage || null);
      // Auto-expand stats if there are warnings/dangers
      if (result.stats?.alertes?.some((a: BrassageAlerte) => a.type !== 'success')) {
        setShowStatsDetail(true);
      }
      setSuccess(result.message);
    } catch (e: any) {
      setErreur(e?.data?.error || 'Erreur lors de la génération du brassage.');
    } finally { setLoading(false); }
  };

  const handleVerification = async () => {
    setVerificationLoading(true); setVerificationReport(null); setShowVerification(true);
    try {
      // Prendre toutes les sessions recentes
      const sessionIds = (allSessions?.sessions ?? []).map(s => s.id);
      const report = await verifierBrassage({ session_ids: sessionIds }).unwrap();
      setVerificationReport(report);
    } catch (e: any) {
      setErreur(e?.data?.error || 'Erreur lors de la verification.');
    } finally { setVerificationLoading(false); }
  };

  // ── Toggle checkbox helpers ──
  const toggleNiveau = (n: string) => {
    setSelectedNiveaux(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
  };
  const togglePole = (p: string) => {
    setSelectedPoles(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };
  const toggleSiteSelection = (id: number) => {
    setSelectedSites(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleClasse = (c: string) => {
    setSelectedClasses(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  // ── Impression ──
  const handlePrintOrPreview = async (doPrint: boolean) => {
    if (!printSessionId) { setErreur('Veuillez sélectionner une session de brassage.'); return; }
    setLoading(true); setErreur('');
    try {
      const printOpts: BrassagePrintOptions = {
        format: printFormat, orientation: printOrientation,
        fontFamily: printFont, fontScale: printFontScale,
        institutVille: infoInstitut?.ville,
        chefScolariteNom: infoInstitut?.chef_scolarite,
        chefScolariteTitre: infoInstitut?.chef_scolarite_titre,
      };
      let html = '';

      if (printMode === 'liste_presence') {
        // Mode listes de présence — une page par salle
        const result = await fetchResultatsParSalle(printSessionId);
        if (result.data && Array.isArray(result.data) && result.data.length > 0) {
          let sallesData = result.data as ResultatsParSalle[];

          // Filtrer par site si sélectionné
          if (presenceSiteFilter) {
            sallesData = sallesData.filter(r => r.site_nom === presenceSiteFilter);
          }
          // Trier par site puis salle
          sallesData.sort((a, b) => a.site_nom.localeCompare(b.site_nom) || a.salle_nom.localeCompare(b.salle_nom));

          // Trouver les créneaux du jour + heure sélectionnés
          const creneauxDuSlot = (presenceSelectedDate && presenceSelectedHeure)
            ? emploiCreneaux.filter(c => c.date === presenceSelectedDate && c.heure_debut === presenceSelectedHeure)
            : [];

          const presenceSalles: PresenceSalleData[] = [];

          for (const salle of sallesData) {
            if (creneauxDuSlot.length > 0) {
              // Identifier les classes présentes dans la salle
              const classesSalle = salle.placements
                .map(p => p.etudiant_classe)
                .filter((c, i, arr) => arr.indexOf(c) === i);

              // Pour chaque créneau, trouver les classes concernées
              const matieres: PresenceMatiereInfo[] = [];
              const classesConcernees = new Set<string>();
              const enseignants: string[] = [];
              let jourSemaine = '';
              let heureFin = '';

              // Mapping pôle nom → code (lowercase) pour le filtre pôle
              const POLE_CODE_MAP: Record<string, string> = {
                'Polytechnique': 'poly',
                'Commerce': 'com',
                'Juridique': 'jur',
              };

              // Index pôle par classe pour vérification cross-pôle
              const classeToPolesMap: Record<string, Set<string>> = {};
              for (const p of salle.placements) {
                if (p.etudiant_classe && p.etudiant_pole) {
                  if (!classeToPolesMap[p.etudiant_classe]) classeToPolesMap[p.etudiant_classe] = new Set();
                  const code = POLE_CODE_MAP[p.etudiant_pole] || p.etudiant_pole.toLowerCase();
                  classeToPolesMap[p.etudiant_classe].add(code);
                }
              }

              for (const creneau of creneauxDuSlot) {
                // Parsing du label (identique au code qui marchait hier)
                const optionCodes = creneau.groupe_options_label
                  ? creneau.groupe_options_label.split('-')
                  : [];

                // Trouver les classes de la salle qui correspondent à ce créneau
                const classesMatch = classesSalle.filter(cls => {
                  const niveauMatch = NIVEAUX.find(n => cls.startsWith(n));
                  if (!niveauMatch || niveauMatch !== creneau.niveau) return false;
                  if (optionCodes.length === 0) return false;
                  const classeOption = cls.substring(niveauMatch.length);
                  if (!optionCodes.some(oc => oc === classeOption)) return false;

                  // Filtre pôle additionnel (comparaison lowercase)
                  const poleCodes: string[] = (creneau.pole_codes ?? []).map((pc: string) => pc.toLowerCase());
                  if (poleCodes.length > 0 && classeToPolesMap[cls]) {
                    const studentPoles = classeToPolesMap[cls];
                    if (!poleCodes.some(pc => studentPoles.has(pc))) return false;
                  }
                  return true;
                });

                if (classesMatch.length > 0) {
                  classesMatch.forEach(c => classesConcernees.add(c));
                  // Fusionner les classes si la matière est déjà présente (plusieurs groupes)
                  const existante = matieres.find(m => m.nom === creneau.matiere_nom);
                  if (existante) {
                    classesMatch.forEach(c => { if (!existante.classes.includes(c)) existante.classes.push(c); });
                    existante.classes.sort();
                  } else {
                    matieres.push({
                      nom: creneau.matiere_nom,
                      classes: classesMatch.sort(),
                    });
                  }
                  creneau.enseignant_noms.forEach(e => { if (!enseignants.includes(e)) enseignants.push(e); });
                  jourSemaine = creneau.jour_semaine;
                  heureFin = creneau.heure_fin;
                }
              }

              // Fix 2026-09-16 : seuls les étudiants dont la classe compose sur ce créneau
              // figurent sur la liste (le brassage mélange niveaux et pôles dans une salle).
              // Une salle sans aucun étudiant concerné n'a pas de liste.
              const placementsConcernes = salle.placements.filter(p => classesConcernees.has(p.etudiant_classe));
              if (placementsConcernes.length === 0) continue;

              presenceSalles.push({
                site_nom: salle.site_nom,
                salle_nom: salle.salle_nom,
                effectif: placementsConcernes.length,
                placements: placementsConcernes,
                creneau: matieres.length > 0 ? {
                  date: presenceSelectedDate,
                  jour_semaine: jourSemaine,
                  heure_debut: presenceSelectedHeure,
                  heure_fin: heureFin,
                  matieres,
                  enseignant_noms: enseignants,
                } : undefined,
              });
            } else {
              // Sans emploi du temps : page vierge (champs à remplir manuellement)
              presenceSalles.push({
                site_nom: salle.site_nom,
                salle_nom: salle.salle_nom,
                effectif: salle.effectif,
                placements: salle.placements,
              });
            }
          }

          if (presenceSalles.length === 0) {
            setErreur(creneauxDuSlot.length > 0
              ? "Aucun étudiant de ce brassage ne compose sur le créneau choisi (vérifiez la session de brassage et le site)."
              : 'Aucune salle trouvée pour le site choisi.');
            setLoading(false);
            return;
          }

          const presOpts: PresencePrintOptions = {
            fontFamily: printFont,
            fontScale: printFontScale,
            sessionLabel: presenceSessionLabel || 'Examen Partiel',
            anneeAcademique: presenceAnnee || anneeAcademique,
            institutVille: infoInstitut?.ville,
            chefScolariteNom: infoInstitut?.chef_scolarite,
            chefScolariteTitre: infoInstitut?.chef_scolarite_titre,
          };
          html = buildListePresenceHTML(presenceSalles, presOpts);
        }
      } else if (printMode === 'par_salle') {
        const result = await fetchResultatsParSalle(printSessionId);
        if (result.data && Array.isArray(result.data)) {
          const groups = buildGroupsParSalle(result.data);
          html = buildBrassageListeHTML(groups, 'par_salle', anneeAcademique, printOpts);
        }
      } else {
        const result = await fetchResultatsParClasse({ sessionId: printSessionId });
        if (result.data && Array.isArray(result.data)) {
          const groups = buildGroupsParClasse(result.data);
          html = buildBrassageListeHTML(groups, 'par_classe', anneeAcademique, printOpts);
        }
      }

      if (!html) { setErreur('Aucune donnée trouvée pour cette session.'); setLoading(false); return; }

      if (doPrint) {
        const w = window.open('', '_blank');
        if (!w) { setErreur("Impossible d'ouvrir la fenêtre. Vérifiez les popups."); setLoading(false); return; }
        w.document.write(html);
        w.document.close();
        setTimeout(() => { w.print(); }, 600);
      } else {
        setPreviewHTML(html);
        setShowPreview(true);
      }
    } catch {
      setErreur('Erreur lors du chargement des résultats.');
    } finally { setLoading(false); }
  };

  // Helper: dates disponibles dans les créneaux d'emploi du temps
  const presenceDatesDisponibles = emploiCreneaux
    .map(c => ({ date: c.date, jour_semaine: c.jour_semaine }))
    .filter((d, i, arr) => arr.findIndex(x => x.date === d.date) === i)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Helper: heures disponibles pour la date sélectionnée
  const presenceHeuresDisponibles = presenceSelectedDate
    ? emploiCreneaux
        .filter(c => c.date === presenceSelectedDate)
        .map(c => ({ heure_debut: c.heure_debut, heure_fin: c.heure_fin }))
        .filter((h, i, arr) => arr.findIndex(x => x.heure_debut === h.heure_debut) === i)
        .sort((a, b) => a.heure_debut.localeCompare(b.heure_debut))
    : [];

  // Helper: matières du créneau sélectionné (pour info)
  const presenceMatieresDuSlot = (presenceSelectedDate && presenceSelectedHeure)
    ? emploiCreneaux
        .filter(c => c.date === presenceSelectedDate && c.heure_debut === presenceSelectedHeure)
        .map(c => ({ matiere_nom: c.matiere_nom, niveau: c.niveau, groupe: c.groupe_options_label }))
    : [];

  // Calculs résumé config
  const totalSalles = sites.reduce((sum, s) => sum + (Number(s.nombre_salles) || 0), 0);
  const totalCapacite = sites.reduce((sum, s) => sum + (Number(s.capacite_totale) || 0), 0);

  // ────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Shuffle size={24} /> Brassage Examens
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Placement des étudiants dans les salles d'examen — Impression des listes brassées
        </p>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-6">
          {([
            { id: 'configuration' as const, label: 'Sites & Salles', icon: <Settings2 size={16} /> },
            { id: 'generation' as const, label: 'Génération', icon: <Shuffle size={16} /> },
            { id: 'impression' as const, label: 'Impression', icon: <Printer size={16} /> },
            { id: 'recherche' as const, label: 'Recherche étudiant', icon: <Search size={16} /> },
            { id: 'ajout_tardif' as const, label: 'Ajout tardif', icon: <UserPlus size={16} /> },
            { id: 'historique' as const, label: 'Historique', icon: <Clock size={16} /> },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Messages */}
      {erreur && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
          {erreur} <button onClick={() => setErreur('')}><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><CheckCircle size={14} /> {success}</span>
          <button onClick={() => setSuccess('')}><X size={14} /></button>
        </div>
      )}

      {/* ═══════════════ ONGLET CONFIGURATION ═══════════════ */}
      {activeTab === 'configuration' && (
        <div className="space-y-5">
          {/* Résumé */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Sites actifs', value: sites.filter(s => s.actif).length, icon: <MapPin size={20} /> },
              { label: 'Salles actives', value: totalSalles, icon: <Building2 size={20} /> },
              { label: 'Capacité totale', value: totalCapacite, icon: <CheckCircle size={20} /> },
            ].map(card => (
              <div key={card.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{card.label}</span>
                  <span className="text-blue-500">{card.icon}</span>
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{card.value}</div>
              </div>
            ))}
          </div>

          {/* Deux panneaux */}
          <div className="grid grid-cols-2 gap-5">
            {/* Panel Sites */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Sites d'examen</h3>
                <button onClick={() => openSiteForm()} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium">
                  <Plus size={14} /> Ajouter
                </button>
              </div>
              {sites.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Aucun site configuré</p>
              ) : (
                <div className="space-y-2">
                  {sites.map(site => (
                    <div
                      key={site.id}
                      onClick={() => setSelectedSiteId(site.id)}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedSiteId === site.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700'
                          : 'bg-gray-50 dark:bg-gray-700/50 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className={!site.actif ? 'opacity-50' : ''}>
                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                          {site.nom}
                          {!site.actif && <span className="ml-1.5 text-xs text-red-500 dark:text-red-400">(inactif)</span>}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {site.nombre_salles} salle{site.nombre_salles !== 1 ? 's' : ''} — {site.capacite_totale} places
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); updateSite({ id: site.id, data: { actif: !site.actif } }); }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                            site.actif ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                          title={site.actif ? 'Désactiver ce site' : 'Activer ce site'}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            site.actif ? 'translate-x-[18px]' : 'translate-x-[2px]'
                          }`} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); openSiteForm(site); }} className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600" title="Modifier">
                          <Edit3 size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: 'site', id: site.id, nom: site.nom }); }} className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" title="Supprimer">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Panel Salles */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Salles {selectedSiteId ? `— ${sites.find(s => s.id === selectedSiteId)?.nom}` : ''}
                </h3>
                {selectedSiteId && (
                  <button onClick={() => openSalleForm()} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium">
                    <Plus size={14} /> Ajouter
                  </button>
                )}
              </div>
              {!selectedSiteId ? (
                <div className="text-center py-6">
                  <MapPin size={28} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm text-gray-400 dark:text-gray-500">Cliquez sur un site à gauche pour gérer ses salles</p>
                </div>
              ) : salles.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-400 dark:text-gray-500 mb-2">Aucune salle pour ce site</p>
                  <button onClick={() => openSalleForm()} className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium">
                    + Ajouter la première salle
                  </button>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      {['Salle', 'Capacité', 'Actif', 'Actions'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {salles.map(salle => (
                      <tr key={salle.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${!salle.actif ? 'opacity-50' : ''}`}>
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                          {salle.nom}
                          {!salle.actif && <span className="ml-1.5 text-xs text-red-500 dark:text-red-400">(inactive)</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{salle.capacite} places</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => updateSalle({ id: salle.id, data: { actif: !salle.actif } })}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                              salle.actif ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                            title={salle.actif ? 'Désactiver cette salle' : 'Activer cette salle'}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              salle.actif ? 'translate-x-[18px]' : 'translate-x-[2px]'
                            }`} />
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button onClick={() => openSalleForm(salle)} className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600" title="Modifier">
                              <Edit3 size={14} />
                            </button>
                            <button onClick={() => setDeleteConfirm({ type: 'salle', id: salle.id, nom: salle.nom })} className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" title="Supprimer">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ ONGLET GÉNÉRATION ═══════════════ */}
      {activeTab === 'generation' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Paramètres du brassage</h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Année académique */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année académique</label>
                <select value={anneeAcademique} onChange={e => setAnneeAcademique(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  {annees.length === 0 && <option value="2024-2025">2024-2025</option>}
                  {annees.map(a => <option key={a.id} value={a.libelle}>{a.libelle}{a.est_active ? ' (active)' : ''}</option>)}
                </select>
              </div>

              {/* Mode */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mode de brassage</label>
                <div className="flex flex-col gap-2 mt-1">
                  {([
                    { val: 'par_niveau' as const, label: 'Par Niveau (mélanger L1, L2, L3...)' },
                    { val: 'par_pole' as const, label: 'Par Pôle (mélanger les pôles)' },
                    { val: 'par_classe' as const, label: 'Par Classe/Option (mélanger les classes)' },
                  ]).map(opt => (
                    <label key={opt.val} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={mode === opt.val} onChange={() => setMode(opt.val)} className="text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Filtres optionnels */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Niveaux (optionnel — tous si vide)</label>
                <div className="flex flex-wrap gap-2">
                  {NIVEAUX.map(n => (
                    <label key={n} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={selectedNiveaux.includes(n)} onChange={() => toggleNiveau(n)} className="rounded text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">{n}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Pôles (optionnel — tous si vide)</label>
                <div className="flex flex-wrap gap-2">
                  {poles.map(p => (
                    <label key={p.id} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={selectedPoles.includes(p.nom)} onChange={() => togglePole(p.nom)} className="rounded text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">{p.nom}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Sites à utiliser */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Sites à utiliser (optionnel — tous si vide)</label>
              <div className="flex flex-wrap gap-2">
                {sites.filter(s => s.actif).map(s => (
                  <label key={s.id} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={selectedSites.includes(s.id)} onChange={() => toggleSiteSelection(s.id)} className="rounded text-blue-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-200">{s.nom} ({s.capacite_totale} pl.)</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Classes filter (visible en mode par_classe) */}
            {mode === 'par_classe' && (() => {
              // Classes filtrées par niveaux + pôles sélectionnés (les classes
              // visibles à l'écran). Les boutons de sélection rapide ne
              // travaillent QUE sur cette liste visible, pas sur toute la BD.
              const classesVisibles = classes.filter(c => {
                if (selectedNiveaux.length > 0 && !selectedNiveaux.includes(c.niveau)) return false;
                if (selectedPoles.length > 0) {
                  const poleName = c.pole_nom || '';
                  if (!selectedPoles.some(p => poleName.toLowerCase().includes(p.toLowerCase()))) return false;
                }
                return true;
              });
              // Formation Initiale (FI) = classes SANS suffixe -CS ni -DD dans
              // le code, et dont l'option ne contient pas "(CS)"/"(DD)". Ex :
              //   L1MI (FI) ✓  |  L1MI-CS (CS) ✗  |  L1DD-AII (DD) ✗
              const estFI = (c: { code: string; option_nom?: string }): boolean => {
                const code = (c.code || '').toUpperCase();
                if (/-(CS|DD)\b/.test(code) || /\bDD-/.test(code)) return false;
                const opt = (c.option_nom || '').toLowerCase();
                if (opt.includes('(cs)') || opt.includes('(dd)')) return false;
                return true;
              };
              const estCS = (c: { code: string; option_nom?: string }): boolean => {
                const code = (c.code || '').toUpperCase();
                if (/-CS\b/.test(code)) return true;
                const opt = (c.option_nom || '').toLowerCase();
                return opt.includes('(cs)') || opt.includes('cours du soir');
              };
              const codesVisibles = classesVisibles.map(c => c.code);
              const codesFI = classesVisibles.filter(estFI).map(c => c.code);
              const codesCS = classesVisibles.filter(estCS).map(c => c.code);
              return (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Classes à inclure (optionnel — toutes si vide)
                </label>
                {/* Boutons de sélection rapide */}
                {classesVisibles.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedClasses(codesVisibles)}
                      className="text-xs px-2 py-1 rounded border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 font-medium"
                      title={`Sélectionner les ${codesVisibles.length} classes visibles`}
                    >
                      ☑ Tout cocher ({codesVisibles.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedClasses(codesFI)}
                      className="text-xs px-2 py-1 rounded border border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 font-medium"
                      title="Sélectionner uniquement les classes de Formation Initiale (exclut CS et DD)"
                    >
                      ☑ FI uniquement ({codesFI.length})
                    </button>
                    {codesCS.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedClasses(codesCS)}
                        className="text-xs px-2 py-1 rounded border border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 font-medium"
                        title="Sélectionner uniquement les classes de Formation Continue / Cours du Soir"
                      >
                        ☑ Formation Continue ({codesCS.length})
                      </button>
                    )}
                    {selectedClasses.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedClasses([])}
                        className="text-xs px-2 py-1 rounded border border-gray-400 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        Tout désélectionner
                      </button>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                  {classes.length === 0 ? (
                    <span className="text-xs text-gray-400">Aucune classe trouvée</span>
                  ) : (
                    classesVisibles.map(c => (
                      <label key={c.id} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={selectedClasses.includes(c.code)} onChange={() => toggleClasse(c.code)} className="rounded text-blue-600" />
                        <span className="text-sm text-gray-700 dark:text-gray-200">
                          {c.code}
                          <span className="text-xs text-gray-400 ml-1">({c.option_nom})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {selectedClasses.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500">
                    {selectedClasses.length} classe(s) sélectionnée(s)
                  </div>
                )}
              </div>
              );
            })()}

            {/* Exclusion des absents des sessions passées */}
            <ExclusionAbsentsPanel anneeCourante={anneeAcademique} />

            {/* Mode rattrapage */}
            <div className={`p-3 rounded-lg border ${modeRattrapage ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700' : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700'}`}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modeRattrapage}
                  onChange={(e) => {
                    setModeRattrapage(e.target.checked);
                    if (e.target.checked) {
                      triggerCandidats({
                        niveaux: selectedNiveaux.length > 0 ? selectedNiveaux : undefined,
                        poles: selectedPoles.length > 0 ? selectedPoles : undefined,
                        classes: selectedClasses.length > 0 ? selectedClasses : undefined,
                        semestre: semestreRattrapage,
                        annee_academique: anneeAcademique,
                      });
                    }
                  }}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white">
                    🎯 Mode rattrapage
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Ne brasser que les étudiants <strong>ajournés</strong> (qui n'ont pas validé leur semestre,
                    selon la délibération). Les étudiants validés par compensation ne reviennent pas.
                  </div>
                  <div className="text-xs text-orange-700 dark:text-orange-300 mt-1.5 italic">
                    💡 <strong>Compactage intelligent des salles</strong> : on utilise uniquement les salles
                    nécessaires (les plus grandes en priorité) pour viser une <strong>occupation équilibrée</strong>
                    (ni vide, ni saturée). Tous les niveaux/pôles sont mélangés selon le mode choisi.
                    <br/>
                    Le slider <strong>Taux de remplissage</strong> ci-dessous devient un <strong>objectif d'occupation cible</strong> :
                    100% = 75% par défaut (compromis), 80% = aéré, 60% = très aéré.
                  </div>
                </div>
              </label>

              {modeRattrapage && (
                <div className="mt-3 pl-6 flex flex-wrap items-center gap-2 text-xs">
                  <label className="font-medium text-gray-700 dark:text-gray-300">Semestre du rattrapage</label>
                  <select
                    value={semestreRattrapage}
                    onChange={(e) => {
                      const s = Number(e.target.value) as 1 | 2;
                      setSemestreRattrapage(s);
                      triggerCandidats({
                        niveaux: selectedNiveaux.length > 0 ? selectedNiveaux : undefined,
                        poles: selectedPoles.length > 0 ? selectedPoles : undefined,
                        classes: selectedClasses.length > 0 ? selectedClasses : undefined,
                        semestre: s,
                        annee_academique: anneeAcademique,
                      });
                    }}
                    className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value={2}>Semestre pair : participants non validés, ou non participants ayant payé 4 mois dont un depuis avril (absents aux deux sessions écartés)</option>
                    <option value={1}>Semestre impair : règle actuelle (ajournés)</option>
                  </select>
                </div>
              )}

              {modeRattrapage && (
                <div className="mt-3 pl-6">
                  {loadingCandidats ? (
                    <div className="text-xs text-gray-500">Comptage des candidats…</div>
                  ) : candidatsRattrapage ? (
                    <div className="space-y-2">
                      <div className="text-sm">
                        <strong className="text-orange-700 dark:text-orange-300 text-lg">{candidatsRattrapage.total}</strong>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">étudiant(s) à rattraper</span>
                      </div>
                      {candidatsRattrapage.par_niveau.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {candidatsRattrapage.par_niveau.map((p) => (
                            <span key={p.niveau} className="px-2 py-0.5 text-xs rounded bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200">
                              {p.niveau} : {p.nb}
                            </span>
                          ))}
                        </div>
                      )}
                      {candidatsRattrapage.rattrapage && (
                        <div className="text-xs space-y-0.5 border-t border-orange-200 dark:border-orange-800 pt-2">
                          <div className="font-semibold text-gray-800 dark:text-gray-200">Admis au brassage</div>
                          {candidatsRattrapage.rattrapage.categories.filter((c) => c.inclus).map((c) => (
                            <div key={c.code} className="flex justify-between gap-3 text-gray-700 dark:text-gray-300">
                              <span>{c.libelle}</span><strong>{c.nb}</strong>
                            </div>
                          ))}
                          <div className="font-semibold text-gray-800 dark:text-gray-200 pt-1">Écartés</div>
                          {candidatsRattrapage.rattrapage.categories.filter((c) => !c.inclus).map((c) => (
                            <div key={c.code} className="flex justify-between gap-3 text-gray-500 dark:text-gray-400">
                              <span>{c.libelle}</span><strong>{c.nb}</strong>
                            </div>
                          ))}
                          {candidatsRattrapage.rattrapage.alertes.map((a, i) => (
                            <div key={i} className="pt-1 text-red-700 dark:text-red-300">{a}</div>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={() => triggerCandidats({
                          niveaux: selectedNiveaux.length > 0 ? selectedNiveaux : undefined,
                          poles: selectedPoles.length > 0 ? selectedPoles : undefined,
                          classes: selectedClasses.length > 0 ? selectedClasses : undefined,
                          semestre: semestreRattrapage,
                          annee_academique: anneeAcademique,
                        })}
                        className="text-xs text-blue-600 hover:underline"
                      >🔄 Rafraîchir</button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Bouton auto-détection TP */}
            <div className="flex items-center justify-between gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
              <div className="text-xs text-blue-800 dark:text-blue-200">
                ℹ Les TP (matières dont le nom commence par « TP ») sont automatiquement <strong>exclues du rattrapage</strong>.
              </div>
              <button
                onClick={async () => {
                  if (!window.confirm('Re-lancer l\'auto-détection des matières TP ?\n\nLes matières dont le nom commence par "TP" seront marquées comme TP.')) return;
                  try {
                    const res = await autoDetecterTp({}).unwrap();
                    alert(res.message);
                  } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
                }}
                disabled={detectingTp}
                className="text-xs px-2 py-1 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-50"
              >
                {detectingTp ? '...' : '🔍 Auto-détecter TP'}
              </button>
            </div>

            {/* Taux de remplissage */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Taux de remplissage des salles : <strong>{tauxRemplissage}%</strong>
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">50%</span>
                <input
                  type="range" min={50} max={100} step={5} value={tauxRemplissage}
                  onChange={e => setTauxRemplissage(Number(e.target.value))}
                  className="flex-1 accent-blue-600"
                />
                <span className="text-xs text-gray-400">100%</span>
                {tauxRemplissage < 100 && (
                  <button onClick={() => setTauxRemplissage(100)} className="text-xs text-blue-500 hover:text-blue-600">Réinitialiser</button>
                )}
              </div>
              {tauxRemplissage < 100 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Chaque salle sera remplie à {tauxRemplissage}% max de sa capacité (ne dépasse jamais la capacité réelle).
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description (optionnel)</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Ex: Brassage session ordinaire juin 2025"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          {/* Résultat de la génération avec stats */}
          {genResult && (
            <div className="space-y-3">
              {/* Message principal */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
                {genResult}
                {lastSessionId && (
                  <button
                    onClick={() => { setPrintSessionId(lastSessionId); setActiveTab('impression'); }}
                    className="ml-3 text-blue-600 dark:text-blue-400 underline hover:no-underline"
                  >
                    Aller a l'impression
                  </button>
                )}
              </div>

              {/* Bilan compactage rattrapage */}
              {genCompactage && genCompactage.actif && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg p-4 text-sm">
                  <div className="font-semibold text-orange-800 dark:text-orange-200 mb-2">
                    🎯 Compactage rattrapage appliqué
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-orange-700 dark:text-orange-300">Salles utilisées</div>
                      <div className="text-2xl font-bold text-orange-800 dark:text-orange-100">
                        {genCompactage.nb_salles_utilisees}
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-700 dark:text-orange-300">Salles ignorées</div>
                      <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                        {genCompactage.nb_salles_ignorees}
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-700 dark:text-orange-300">Occupation estimée</div>
                      <div className="text-2xl font-bold text-orange-800 dark:text-orange-100">
                        {genCompactage.taux_occupation_estime}%
                      </div>
                      {genCompactage.occupation_cible_pct && (
                        <div className="text-[10px] text-orange-600">cible : {genCompactage.occupation_cible_pct}%</div>
                      )}
                    </div>
                    <div>
                      <div className="text-orange-700 dark:text-orange-300">Capacité utilisée</div>
                      <div className="text-2xl font-bold text-orange-800 dark:text-orange-100">
                        {genCompactage.capacite_utilisee}
                      </div>
                      <div className="text-[10px] text-orange-600">/ {genCompactage.capacite_disponible_initiale}</div>
                    </div>
                  </div>
                  {genCompactage.salles_ignorees_noms?.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs cursor-pointer text-orange-700 dark:text-orange-300 hover:underline">
                        Voir les {genCompactage.nb_salles_ignorees} salle(s) non utilisée(s)
                      </summary>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {genCompactage.salles_ignorees_noms.map((nom: string, i: number) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-700 text-gray-700 dark:text-gray-300">
                            {nom}
                          </span>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Alertes post-generation */}
              {genStats && genStats.alertes.length > 0 && (
                <div className="space-y-2">
                  {genStats.alertes.map((alerte, i) => (
                    <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm border ${
                      alerte.type === 'danger'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200'
                        : alerte.type === 'warning'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-800 dark:text-green-200'
                    }`}>
                      {alerte.type === 'danger' ? <XCircle size={16} className="mt-0.5 shrink-0" />
                       : alerte.type === 'warning' ? <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                       : <CheckCircle size={16} className="mt-0.5 shrink-0" />}
                      <span>{alerte.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Stats detaillees */}
              {genStats && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowStatsDetail(!showStatsDetail)}
                    className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <div className="flex items-center gap-2">
                      <Shield size={16} />
                      Statistiques detaillees
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        genStats.taux_couverture === 100
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                          : genStats.taux_couverture >= 95
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                      }`}>
                        Couverture : {genStats.taux_couverture}%
                      </span>
                    </div>
                    {showStatsDetail ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {showStatsDetail && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                      {/* Chiffres cles */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{genStats.total_en_base}</div>
                          <div className="text-xs text-gray-500">En base</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{genStats.total_places}</div>
                          <div className="text-xs text-gray-500">Places</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                          <div className={`text-lg font-bold ${genStats.total_non_places > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {genStats.total_non_places}
                          </div>
                          <div className="text-xs text-gray-500">Non places</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">{genStats.capacite_effective}</div>
                          <div className="text-xs text-gray-500">Capacite ({genStats.nombre_salles} salles)</div>
                        </div>
                      </div>

                      {/* Classes oubliees */}
                      {genStats.classes_oubliees.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 mb-2 uppercase">Classes non incluses dans le brassage</h4>
                          <div className="flex flex-wrap gap-2">
                            {genStats.classes_oubliees.map(c => (
                              <span key={c.classe} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded text-xs font-medium">
                                {c.classe} ({c.effectif})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tableau par classe */}
                      {genStats.stats_par_classe.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase">Detail par classe</h4>
                          <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                                <tr>
                                  <th className="text-left px-3 py-2">Classe</th>
                                  <th className="text-right px-3 py-2">En base</th>
                                  <th className="text-right px-3 py-2">Genere</th>
                                  <th className="text-right px-3 py-2">Manquant</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {genStats.stats_par_classe.map(s => (
                                  <tr key={s.classe} className={s.manquant > 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                                    <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-gray-100">{s.classe}</td>
                                    <td className="px-3 py-1.5 text-right text-gray-600 dark:text-gray-400">{s.en_base}</td>
                                    <td className="px-3 py-1.5 text-right text-green-600">{s.genere}</td>
                                    <td className={`px-3 py-1.5 text-right font-medium ${s.manquant > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                      {s.manquant > 0 ? s.manquant : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Bouton voir étudiants non placés */}
                      {genStats.total_non_places > 0 && lastSessionId && (
                        <div>
                          <button
                            onClick={() => setShowNonPlaces(!showNonPlaces)}
                            className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                          >
                            <Users size={16} />
                            Voir les {genStats.total_non_places} étudiant(s) non placé(s)
                            {showNonPlaces ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Panel étudiants non placés ── */}
              {showNonPlaces && lastSessionId && (
                <EtudiantsNonPlacesPanel
                  sessionId={lastSessionId}
                  placementEnCours={placementEnCours}
                  setPlacementEnCours={setPlacementEnCours}
                  placementSalleId={placementSalleId}
                  setPlacementSalleId={setPlacementSalleId}
                />
              )}
            </div>
          )}

          {/* Boutons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerer}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              <Shuffle size={16} /> {loading ? 'Generation en cours...' : 'Generer le Brassage'}
            </button>
            <button
              onClick={handleVerification}
              disabled={verificationLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-650 disabled:opacity-50 text-sm font-medium"
            >
              <Shield size={16} /> {verificationLoading ? 'Verification...' : 'Verifier la couverture globale'}
            </button>
          </div>

          {/* Rapport de verification globale */}
          {showVerification && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Users size={16} /> Verification de couverture globale
                </h3>
                <button onClick={() => setShowVerification(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>

              {verificationLoading ? (
                <div className="text-sm text-gray-500 animate-pulse">Analyse en cours...</div>
              ) : verificationReport ? (
                <div className="space-y-4">
                  {/* Chiffres */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{verificationReport.total_etudiants_actifs}</div>
                      <div className="text-xs text-gray-500">Etudiants actifs (FI+DD)</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                      <div className="text-lg font-bold text-green-600">{verificationReport.total_places}</div>
                      <div className="text-xs text-gray-500">Places</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                      <div className={`text-lg font-bold ${verificationReport.total_manquants > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {verificationReport.total_manquants}
                      </div>
                      <div className="text-xs text-gray-500">Manquants</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                      <div className={`text-lg font-bold ${
                        verificationReport.taux_couverture === 100 ? 'text-green-600'
                        : verificationReport.taux_couverture >= 95 ? 'text-amber-600'
                        : 'text-red-600'
                      }`}>{verificationReport.taux_couverture}%</div>
                      <div className="text-xs text-gray-500">Couverture</div>
                    </div>
                  </div>

                  {/* Alertes */}
                  {verificationReport.alertes.map((a, i) => (
                    <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm border ${
                      a.type === 'danger'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200'
                        : a.type === 'warning'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-800 dark:text-green-200'
                    }`}>
                      {a.type === 'danger' ? <XCircle size={16} className="mt-0.5 shrink-0" />
                       : a.type === 'warning' ? <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                       : <CheckCircle size={16} className="mt-0.5 shrink-0" />}
                      <span>{a.message}</span>
                    </div>
                  ))}

                  {/* Sessions analysees */}
                  {verificationReport.sessions_analysees.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase">Sessions analysees</h4>
                      <div className="space-y-1">
                        {verificationReport.sessions_analysees.map(s => (
                          <div key={s.session_id} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-750 rounded px-3 py-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">#{s.session_id} — {s.description}</span>
                            <span className="text-gray-500">{s.nombre_etudiants} etudiants</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Liste des manquants */}
                  {verificationReport.etudiants_manquants.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 mb-2 uppercase">
                        Etudiants manquants ({verificationReport.etudiants_manquants.length})
                      </h4>
                      <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2">Matricule</th>
                              <th className="text-left px-3 py-2">Nom</th>
                              <th className="text-left px-3 py-2">Prenoms</th>
                              <th className="text-left px-3 py-2">Classe</th>
                              <th className="text-left px-3 py-2">Pole</th>
                              <th className="text-left px-3 py-2">Type</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {verificationReport.etudiants_manquants.map((e, i) => (
                              <tr key={i} className="bg-red-50/50 dark:bg-red-900/5">
                                <td className="px-3 py-1.5 font-mono text-gray-900 dark:text-gray-100">{e.matricule}</td>
                                <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-gray-100">{e.nom}</td>
                                <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{e.prenoms}</td>
                                <td className="px-3 py-1.5">
                                  <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs">{e.classe}</span>
                                </td>
                                <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{e.pole}</td>
                                <td className="px-3 py-1.5 text-gray-500">{e.type_formation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Repartition */}
                      {verificationReport.repartition_manquants.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {verificationReport.repartition_manquants.map((r, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded text-xs font-medium">
                              {r.pole} / {r.niveau}: {r.count}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ ONGLET IMPRESSION ═══════════════ */}
      {activeTab === 'impression' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Impression des listes brassées</h3>

            <div className="grid grid-cols-1 gap-4">
              {/* Session */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Session de brassage</label>
                <select
                  value={printSessionId ?? ''}
                  onChange={e => setPrintSessionId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  {(allSessions?.sessions ?? []).map(s => {
                    const f = (s.filtres_json || {}) as Record<string, any>;
                    const niveaux = (f.niveaux as string[] || []).join(', ');
                    const poles = (f.poles as string[] || []).join(', ');
                    return (
                      <option key={s.id} value={s.id}>
                        {s.description || `Session #${s.id}`} — {s.mode_display} — {s.nombre_etudiants} ét. / {s.nombre_salles_utilisees} salles{niveaux ? ` — Niv: ${niveaux}` : ''}{poles ? ` — Pôles: ${poles}` : ''} — {new Date(s.date_generation).toLocaleDateString('fr-FR')}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Info session sélectionnée */}
              {printSessionId && (() => {
                const session = (allSessions?.sessions ?? []).find(s => s.id === printSessionId);
                if (!session) return null;
                const f = (session.filtres_json || {}) as Record<string, any>;
                const niveaux = (f.niveaux as string[] || []);
                const poles = (f.poles as string[] || []);
                const classes = (f.classes as string[] || []);
                return (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-blue-600 dark:text-blue-400 text-xs">Description</span>
                        <div className="font-medium text-gray-900 dark:text-white">{session.description || `Session #${session.id}`}</div>
                      </div>
                      <div>
                        <span className="text-blue-600 dark:text-blue-400 text-xs">Mode / Étudiants</span>
                        <div className="font-medium text-gray-900 dark:text-white">{session.mode_display} — {session.nombre_etudiants} ét. / {session.nombre_salles_utilisees} salles</div>
                      </div>
                      <div>
                        <span className="text-blue-600 dark:text-blue-400 text-xs">Niveaux / Pôles</span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {niveaux.length > 0 && <span>{niveaux.join(', ')}</span>}
                          {poles.length > 0 && <span className="ml-2 text-gray-500">({poles.join(', ')})</span>}
                          {classes.length > 0 && <span className="ml-2 text-xs text-gray-400">{classes.length} classes</span>}
                          {niveaux.length === 0 && poles.length === 0 && classes.length === 0 && <span className="text-gray-400">Tous</span>}
                        </div>
                      </div>
                      <div>
                        <span className="text-blue-600 dark:text-blue-400 text-xs">Date</span>
                        <div className="font-medium text-gray-900 dark:text-white">{new Date(session.date_generation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Mode impression */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mode d'impression</label>
                <div className="flex gap-3 mt-1 flex-wrap">
                  {([
                    { val: 'par_salle' as const, label: 'Listes brassées par Salle' },
                    { val: 'par_classe' as const, label: 'Listes brassées par Classe' },
                    { val: 'liste_presence' as const, label: 'Listes de Présence' },
                  ]).map(opt => (
                    <label key={opt.val} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={printMode === opt.val} onChange={() => setPrintMode(opt.val)} className="text-blue-600" />
                      <span className={`text-sm ${opt.val === 'liste_presence' ? 'font-semibold text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-200'}`}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Format, Orientation, Police, Taille */}
            <div className="flex flex-wrap gap-4 items-end pt-2 border-t border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Format</label>
                <div className="flex gap-2">
                  {(['A4', 'A3'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setPrintFormat(f)}
                      className={`px-3 py-2 text-sm rounded-lg border ${printFormat === f ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Orientation</label>
                <div className="flex gap-2">
                  {([
                    { val: 'portrait' as const, label: 'Portrait' },
                    { val: 'landscape' as const, label: 'Paysage' },
                  ]).map(o => (
                    <button
                      key={o.val}
                      onClick={() => setPrintOrientation(o.val)}
                      className={`px-3 py-2 text-sm rounded-lg border ${printOrientation === o.val ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Police</label>
                <select
                  value={printFont}
                  onChange={e => setPrintFont(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {[
                    { value: "'Arial', Helvetica, sans-serif", label: 'Arial' },
                    { value: "'Calibri', 'Segoe UI', sans-serif", label: 'Calibri' },
                    { value: "'Times New Roman', serif", label: 'Times New Roman' },
                    { value: "'Georgia', serif", label: 'Georgia' },
                    { value: "'Verdana', sans-serif", label: 'Verdana' },
                    { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
                    { value: "'Tahoma', sans-serif", label: 'Tahoma' },
                    { value: "'Courier New', monospace", label: 'Courier New' },
                  ].map(f => (
                    <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Taille ({printFontScale}%)</label>
                <div className="flex items-center gap-2">
                  <input type="range" min={70} max={150} step={5} value={printFontScale} onChange={e => setPrintFontScale(Number(e.target.value))} className="w-24" />
                  <button onClick={() => setPrintFontScale(100)} className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Reset</button>
                </div>
              </div>
            </div>

            {/* ── Options spécifiques aux Listes de Présence ── */}
            {printMode === 'liste_presence' && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-green-600" />
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-400">Options des Listes de Présence</h4>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Intitulé de la session */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Intitulé de la session d'examen</label>
                    <input
                      type="text"
                      value={presenceSessionLabel}
                      onChange={e => setPresenceSessionLabel(e.target.value)}
                      placeholder="Ex: Examen Partiel: Session Ordinaire d'Août 2025"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  {/* Année académique */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année académique</label>
                    <input
                      type="text"
                      value={presenceAnnee}
                      onChange={e => setPresenceAnnee(e.target.value)}
                      placeholder="Ex: 2024-2025"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  {/* Filtre par site */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Imprimer par site</label>
                    <select
                      value={presenceSiteFilter}
                      onChange={e => setPresenceSiteFilter(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Tous les sites</option>
                      {sites.map(s => (
                        <option key={s.id} value={s.nom}>{s.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Session d'emploi du temps */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Session d'emploi du temps <span className="text-red-500 font-bold">*</span> <span className="text-gray-400">(requis pour pré-remplir matières, date & horaire)</span>
                  </label>
                  <select
                    value={presenceEmploiSessionId ?? ''}
                    onChange={e => {
                      const val = e.target.value ? Number(e.target.value) : null;
                      setPresenceEmploiSessionId(val);
                      setPresenceSelectedDate('');
                      setPresenceSelectedHeure('');
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">-- Sans emploi du temps (champs vides à remplir manuellement) --</option>
                    {emploiSessions.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.nom} — {s.annee_academique} S{s.semestre} — {s.type_session} — {s.nombre_creneaux} créneaux ({s.statut})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sélection du jour et de l'heure */}
                {presenceEmploiSessionId && emploiCreneaux.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Jour */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Jour de l'épreuve</label>
                      <select
                        value={presenceSelectedDate}
                        onChange={e => { setPresenceSelectedDate(e.target.value); setPresenceSelectedHeure(''); }}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">-- Sélectionner le jour --</option>
                        {presenceDatesDisponibles.map(d => (
                          <option key={d.date} value={d.date}>
                            {d.jour_semaine} {d.date.split('-').reverse().join('/')}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Heure */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Heure de l'épreuve</label>
                      <select
                        value={presenceSelectedHeure}
                        onChange={e => setPresenceSelectedHeure(e.target.value)}
                        disabled={!presenceSelectedDate}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                      >
                        <option value="">-- Sélectionner l'heure --</option>
                        {presenceHeuresDisponibles.map(h => (
                          <option key={h.heure_debut} value={h.heure_debut}>
                            {h.heure_debut.replace(/^(\d{2}):(\d{2}).*$/, '$1H$2')} — {h.heure_fin.replace(/^(\d{2}):(\d{2}).*$/, '$1H$2')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Résumé des matières du créneau sélectionné */}
                {presenceMatieresDuSlot.length > 0 && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                    <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">
                      Matières programmées pour ce créneau :
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {presenceMatieresDuSlot.map((m, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 border border-green-300 dark:border-green-600 text-gray-800 dark:text-gray-200">
                          <span className="font-bold">{m.matiere_nom}</span>
                          <span className="text-green-600 dark:text-green-400">({m.niveau} — {m.groupe})</span>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-500 mt-1.5 italic">
                      Chaque salle affichera uniquement les matières correspondant aux classes qui y sont présentes.
                    </p>
                  </div>
                )}

                {presenceEmploiSessionId && emploiCreneaux.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    Aucun créneau trouvé pour cette session d'emploi du temps.
                  </p>
                )}
              </div>
            )}

            {/* Avertissement si emploi du temps / date / heure non sélectionnés */}
            {printMode === 'liste_presence' && (!presenceEmploiSessionId || !presenceSelectedDate || !presenceSelectedHeure) && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Matières non pré-remplies.</strong> Pour que les modules, la date et l'heure soient automatiquement remplis, vous devez sélectionner :
                  <ul className="list-disc ml-4 mt-1 text-xs">
                    {!presenceEmploiSessionId && <li>La session d'emploi du temps</li>}
                    {presenceEmploiSessionId && !presenceSelectedDate && <li>Le jour de l'épreuve</li>}
                    {presenceEmploiSessionId && presenceSelectedDate && !presenceSelectedHeure && <li>L'heure de l'épreuve</li>}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button onClick={() => handlePrintOrPreview(false)} disabled={loading || !printSessionId}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
              <Eye size={16} /> {loading ? 'Chargement...' : 'Prévisualiser'}
            </button>
            <button onClick={() => handlePrintOrPreview(true)} disabled={loading || !printSessionId}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium">
              <Printer size={16} /> Imprimer
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════ ONGLET RECHERCHE ÉTUDIANT ═══════════════ */}
      {activeTab === 'recherche' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
              <Search size={20} className="text-blue-500" />
              Rechercher un étudiant dans les listes brassées
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Choisissez la session de brassage, puis saisissez le nom, les prénoms ou le matricule pour trouver la salle d'affectation.
            </p>

            {/* Sélection de la session de brassage */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Session de brassage
              </label>
              <select
                value={rechercheSessionId ?? ''}
                onChange={(e) => setRechercheSessionId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Choisir une session de brassage --</option>
                {(allSessions?.sessions ?? []).map((s) => {
                  const f = (s.filtres_json || {}) as Record<string, any>;
                  const niveaux = (f.niveaux as string[] || []).join(', ');
                  const poles = (f.poles as string[] || []).join(', ');
                  return (
                    <option key={s.id} value={s.id}>
                      {s.description || `Session #${s.id}`} — {s.mode_display || s.mode} — {s.nombre_etudiants} ét. / {s.nombre_salles_utilisees} salles{niveaux ? ` — Niv: ${niveaux}` : ''}{poles ? ` — Pôles: ${poles}` : ''} — {new Date(s.date_generation).toLocaleDateString('fr-FR')}
                    </option>
                  );
                })}
              </select>
              {!rechercheSessionId && (allSessions?.sessions ?? []).length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Veuillez sélectionner une session pour effectuer la recherche
                </p>
              )}
              {(allSessions?.sessions ?? []).length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Aucune session de brassage disponible. Générez un brassage d'abord.
                </p>
              )}
            </div>

            {/* Infos de la session sélectionnée */}
            {rechercheSessionId && (() => {
              const session = (allSessions?.sessions ?? []).find(s => s.id === rechercheSessionId);
              if (!session) return null;
              return (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-blue-600 dark:text-blue-400 text-xs">Description</span>
                      <div className="font-medium text-gray-900 dark:text-white">{session.description || `Session #${session.id}`}</div>
                    </div>
                    <div>
                      <span className="text-blue-600 dark:text-blue-400 text-xs">Mode</span>
                      <div className="font-medium text-gray-900 dark:text-white">{session.mode_display || session.mode}</div>
                    </div>
                    <div>
                      <span className="text-blue-600 dark:text-blue-400 text-xs">Étudiants</span>
                      <div className="font-medium text-gray-900 dark:text-white">{session.nombre_etudiants}</div>
                    </div>
                    <div>
                      <span className="text-blue-600 dark:text-blue-400 text-xs">Date</span>
                      <div className="font-medium text-gray-900 dark:text-white">{new Date(session.date_generation).toLocaleDateString('fr-FR')}</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Champ de recherche */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (rechercheQuery.trim().length >= 2 && rechercheSessionId) {
                  rechercherEtudiant({ q: rechercheQuery.trim(), session_id: rechercheSessionId });
                }
              }}
              className="flex gap-3"
            >
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={rechercheQuery}
                  onChange={(e) => setRechercheQuery(e.target.value)}
                  placeholder="Nom, prénoms ou matricule..."
                  disabled={!rechercheSessionId}
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <button
                type="submit"
                disabled={rechercheQuery.trim().length < 2 || rechercheLoading || !rechercheSessionId}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rechercheLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Rechercher
              </button>
            </form>
          </div>

          {/* Résultats */}
          {rechercheData && rechercheSessionId && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {rechercheData.total} résultat{rechercheData.total > 1 ? 's' : ''} trouvé{rechercheData.total > 1 ? 's' : ''}
                </span>
                {rechercheData.session && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {rechercheData.session.description || `Session #${rechercheData.session.id}`}
                    {' — '}
                    {new Date(rechercheData.session.date_generation).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>

              {rechercheData.resultats.length === 0 ? (
                <div className="py-12 text-center">
                  <XCircle size={40} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-500 dark:text-gray-400">Aucun étudiant trouvé pour "{rechercheQuery}"</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Vérifiez l'orthographe ou essayez avec le matricule</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        {['Matricule', 'Nom & Prénoms', 'Classe', 'Niveau', 'Pôle', 'Site', 'Salle'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {rechercheData.resultats.map((r) => (
                        <tr key={r.id} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                          <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">{r.matricule || '—'}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{r.nom} {r.prenoms}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {r.classe}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{r.niveau || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{r.pole || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex items-center gap-1"><MapPin size={12} /> {r.site}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-bold rounded-lg bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                              <Building2 size={14} /> {r.salle}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ ONGLET AJOUT TARDIF ═══════════════ */}
      {activeTab === 'ajout_tardif' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
              <UserPlus size={20} className="text-green-500" />
              Ajouter un étudiant à une liste brassée
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Pour les étudiants inscrits après le brassage. Recherchez l'étudiant non placé, choisissez une salle, puis validez.
            </p>

            {/* Sélection de la session */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Session de brassage</label>
              <select
                value={ajoutSessionId ?? ''}
                onChange={e => { setAjoutSessionId(e.target.value ? Number(e.target.value) : null); setAjoutSuccess(''); setAjoutErreur(''); }}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">-- Choisir une session de brassage --</option>
                {(allSessions?.sessions ?? []).map(s => {
                  const f = (s.filtres_json || {}) as Record<string, any>;
                  const niveaux = (f.niveaux as string[] || []).join(', ');
                  const poles = (f.poles as string[] || []).join(', ');
                  return (
                    <option key={s.id} value={s.id}>
                      {s.description || `Session #${s.id}`} — {s.mode_display || s.mode} — {s.nombre_etudiants} ét. / {s.nombre_salles_utilisees} salles{niveaux ? ` — Niv: ${niveaux}` : ''}{poles ? ` — Pôles: ${poles}` : ''} — {new Date(s.date_generation).toLocaleDateString('fr-FR')}
                    </option>
                  );
                })}
              </select>
            </div>

            {ajoutSessionId && (
              <>
                {/* Recherche d'étudiant non placé */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Rechercher l'étudiant à ajouter</label>
                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      if (ajoutQuery.trim().length >= 2 && ajoutSessionId) {
                        fetchNonPlaces({ sessionId: ajoutSessionId, q: ajoutQuery.trim() });
                      }
                    }}
                    className="flex gap-3"
                  >
                    <div className="relative flex-1">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={ajoutQuery}
                        onChange={e => setAjoutQuery(e.target.value)}
                        placeholder="Nom, prénoms ou matricule de l'étudiant non placé..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={ajoutQuery.trim().length < 2 || nonPlacesLoading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {nonPlacesLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                      Chercher
                    </button>
                  </form>
                </div>

                {/* Sélection de la salle */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Salle d'affectation
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (choisissez « Auto » pour que le système équilibre les salles selon le mode de brassage)
                    </span>
                  </label>
                  <select
                    value={ajoutSalleId ?? '-1'}
                    onChange={e => setAjoutSalleId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {/* Option auto = équilibrage automatique par le backend */}
                    <option value="-1">🎯 Auto — équilibrer selon le mode de la session</option>
                    <option value="">-- Ou choisir manuellement une salle --</option>
                    {sites.map(site => (
                      <optgroup key={site.id} label={site.nom}>
                        {allSalles.filter(s => s.site === site.id && s.actif).map(salle => (
                          <option key={salle.id} value={salle.id}>
                            {salle.nom} (capacité: {salle.capacite})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Messages */}
                {ajoutSuccess && (
                  <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle size={16} /> {ajoutSuccess}
                  </div>
                )}
                {ajoutErreur && (
                  <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
                    <XCircle size={16} /> {ajoutErreur}
                  </div>
                )}

                {/* Résultats — étudiants non placés */}
                {nonPlacesData && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {nonPlacesData.total} étudiant{nonPlacesData.total > 1 ? 's' : ''} non placé{nonPlacesData.total > 1 ? 's' : ''} trouvé{nonPlacesData.total > 1 ? 's' : ''}
                      </span>
                    </div>

                    {nonPlacesData.resultats.length === 0 ? (
                      <div className="py-10 text-center">
                        <CheckCircle size={36} className="mx-auto mb-3 text-green-300 dark:text-green-600" />
                        <p className="text-gray-500 dark:text-gray-400">Tous les étudiants correspondants sont déjà placés</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                              {['Matricule', 'Nom & Prénoms', 'Classe', 'Niveau', 'Action'].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {nonPlacesData.resultats.map(etud => (
                              <tr key={etud.id} className="hover:bg-green-50/50 dark:hover:bg-green-900/10 transition-colors">
                                <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">{etud.matricule}</td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{etud.nom} {etud.prenoms}</td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{etud.classe}</span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{etud.niveau}</td>
                                <td className="px-4 py-3">
                                  <button
                                    disabled={ajoutSalleId === null || ajoutSalleId === undefined}
                                    onClick={async () => {
                                      if (!ajoutSessionId) return;
                                      const isAuto = ajoutSalleId === -1;
                                      if (!isAuto && !ajoutSalleId) return;
                                      setAjoutSuccess(''); setAjoutErreur('');
                                      try {
                                        const body = isAuto
                                          ? { sessionId: ajoutSessionId, matricule: etud.matricule, auto: true }
                                          : { sessionId: ajoutSessionId, matricule: etud.matricule, salle_id: ajoutSalleId as number };
                                        const res = await ajouterEtudiant(body).unwrap();
                                        setAjoutSuccess(res.message);
                                        // Relancer la recherche pour mettre à jour la liste
                                        fetchNonPlaces({ sessionId: ajoutSessionId, q: ajoutQuery.trim() });
                                      } catch (e: any) {
                                        setAjoutErreur(e?.data?.error || "Erreur lors de l'ajout.");
                                      }
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <UserPlus size={14} /> {ajoutSalleId === -1 ? 'Ajouter (Auto)' : 'Ajouter'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ─── Bloc DÉPLACEMENT d'étudiants déjà placés ─── */}
          {ajoutSessionId && (
            <DeplacementBloc sessionId={ajoutSessionId} />
          )}
        </div>
      )}

      {/* ═══════════════ ONGLET HISTORIQUE ═══════════════ */}
      {activeTab === 'historique' && (
        <div className="space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <span className="text-xs text-gray-500 dark:text-gray-400">Total brassages</span>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{historique?.total ?? 0}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <span className="text-xs text-gray-500 dark:text-gray-400">Ce mois</span>
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{historique?.ce_mois ?? 0}</div>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex gap-3 flex-wrap">
              <input type="text" value={histAnnee} onChange={e => setHistAnnee(e.target.value)} placeholder="Année (ex: 2024-2025)"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-40" />
              <input type="date" value={histDateDebut} onChange={e => setHistDateDebut(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              <input type="date" value={histDateFin} onChange={e => setHistDateFin(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          {/* Table enrichie avec couverture */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="w-full text-sm min-w-[1080px]">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Date', 'Filtres', 'Description', 'Brasses', 'Inscrits', 'Couverture', 'Alerte', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(historique?.sessions ?? []).length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucun brassage enregistré</td></tr>
                ) : (historique?.sessions ?? []).map(s => {
                  const couv = s.couverture;
                  const tauxOk = couv && couv.taux_couverture === 100;
                  const tauxWarn = couv && couv.taux_couverture >= 95 && couv.taux_couverture < 100;
                  const tauxDanger = couv && couv.taux_couverture < 95;

                  return (
                    <React.Fragment key={s.id}>
                    <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                      couv?.alerte?.type === 'danger' ? 'bg-red-50/30 dark:bg-red-900/5' : ''
                    }`}>
                      {/* Date */}
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                        {new Date(s.date_generation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>

                      {/* Filtres (poles/niveaux) */}
                      <td className="px-3 py-3 max-w-40">
                        <div className="flex flex-wrap gap-1">
                          {couv?.filtres_label ? (
                            couv.filtres_label.split(' | ').map((f, i) => (
                              <span key={i} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {f}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>

                      {/* Description (cliquable pour ouvrir détails complets) */}
                      <td className="px-3 py-3 text-gray-600 dark:text-gray-400 max-w-[220px] text-xs align-top">
                        <button
                          type="button"
                          onClick={() => setDetailsSessionId(s.id)}
                          title={s.description ? `${s.description}\n\nCliquer pour ouvrir les détails complets et modifier la description.` : 'Cliquer pour ajouter une description et voir les détails.'}
                          className="text-left w-full hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                        >
                          {s.description ? (
                            <div className="line-clamp-2 whitespace-pre-wrap break-words">{s.description}</div>
                          ) : (
                            <span className="italic text-gray-400">Sans description — cliquer pour ajouter</span>
                          )}
                        </button>
                        {s.visible_aux_etudiants && (
                          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-600 text-white" title={`Visible depuis ${s.date_visibilite ? new Date(s.date_visibilite).toLocaleString('fr-FR') : ''} par ${s.visibilite_par_nom || ''}`}>
                            👁 Portail
                          </span>
                        )}
                      </td>

                      {/* Brasses */}
                      <td className="px-3 py-3 text-center">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{s.nombre_etudiants}</span>
                        <div className="text-xs text-gray-400">{s.nombre_salles_utilisees} salles</div>
                      </td>

                      {/* Inscrits en base */}
                      <td className="px-3 py-3 text-center">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{couv?.total_en_base ?? '-'}</span>
                      </td>

                      {/* Couverture */}
                      <td className="px-3 py-3 text-center">
                        {couv ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                              tauxOk ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : tauxWarn ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            }`}>
                              {couv.taux_couverture}%
                            </span>
                            {couv.total_manquants > 0 && (
                              <button
                                onClick={() => setHistNonPlacesSessionId(histNonPlacesSessionId === s.id ? null : s.id)}
                                className="text-xs text-red-500 font-medium hover:underline flex items-center gap-0.5"
                              >
                                <Users size={10} />
                                {couv.total_manquants} manquant(s)
                                {histNonPlacesSessionId === s.id ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                              </button>
                            )}
                          </div>
                        ) : '-'}
                      </td>

                      {/* Alerte */}
                      <td className="px-3 py-3">
                        {couv?.alerte ? (
                          <div className={`flex items-start gap-1.5 text-xs ${
                            couv.alerte.type === 'danger' ? 'text-red-600 dark:text-red-400'
                            : couv.alerte.type === 'warning' ? 'text-amber-600 dark:text-amber-400'
                            : 'text-green-600 dark:text-green-400'
                          }`}>
                            {couv.alerte.type === 'danger' ? <XCircle size={14} className="shrink-0 mt-0.5" />
                             : couv.alerte.type === 'warning' ? <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                             : <CheckCircle size={14} className="shrink-0 mt-0.5" />}
                            <div>
                              <span>{couv.alerte.message}</span>
                              {couv.classes_oubliees.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {couv.classes_oubliees.slice(0, 5).map(c => (
                                    <span key={c.classe} className="px-1 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs">
                                      {c.classe} ({c.effectif})
                                    </span>
                                  ))}
                                  {couv.classes_oubliees.length > 5 && (
                                    <span className="text-xs text-gray-400">+{couv.classes_oubliees.length - 5} autres</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                            <CheckCircle size={14} /> OK
                          </span>
                        )}
                      </td>

                      {/* Actions — colonne compacte, sticky à droite pour rester visible */}
                      <td className="px-2 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setDetailsSessionId(s.id)}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300"
                            title="Voir la répartition détaillée (sites, salles, capacités) et modifier la description"
                          >
                            <Info size={12} /> Détails
                          </button>
                          <button
                            onClick={() => handleToggleVisibiliteBrassage(s)}
                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium ${
                              s.visible_aux_etudiants
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200'
                            }`}
                            title={s.visible_aux_etudiants ? 'Masquer aux étudiants' : 'Rendre visible aux étudiants'}
                          >
                            <Eye size={12} /> {s.visible_aux_etudiants ? 'Visible' : 'Masqué'}
                          </button>
                          <button
                            onClick={() => { setPrintSessionId(s.id); setActiveTab('impression'); }}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                            title="Imprimer les listes"
                          >
                            <Printer size={12} /> Imprimer
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Supprimer ce brassage du ${new Date(s.date_generation).toLocaleDateString('fr-FR')} (${s.nombre_etudiants} etudiants, ${s.nombre_salles_utilisees} salles) ?\n\nCette action est irreversible.`)) return;
                              try {
                                await deleteSession(s.id).unwrap();
                              } catch {
                                alert('Erreur lors de la suppression');
                              }
                            }}
                            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 hover:text-red-700"
                            title="Supprimer ce brassage"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Panel étudiants non placés (expandable) */}
                    {histNonPlacesSessionId === s.id && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <EtudiantsNonPlacesPanel
                            sessionId={s.id}
                            placementEnCours={placementEnCours}
                            setPlacementEnCours={setPlacementEnCours}
                            placementSalleId={placementSalleId}
                            setPlacementSalleId={setPlacementSalleId}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL PRÉVISUALISATION ═══════════════ */}
      {showPreview && previewHTML && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto">
          <div className="my-4 w-full max-w-5xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-t-xl">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Aperçu — {printMode === 'liste_presence' ? 'Listes de Présence' : `Listes brassées ${printMode === 'par_salle' ? 'par salle' : 'par classe'}`}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handlePrintOrPreview(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium">
                  <Printer size={14} /> Imprimer
                </button>
                <button onClick={() => setShowPreview(false)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400"><X size={18} /></button>
              </div>
            </div>
            <div className="overflow-auto max-h-[85vh]">
              <BrassageListePreview html={previewHTML} />
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL FORM SITE ═══════════════ */}
      {showSiteForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{editingSite ? 'Modifier le site' : 'Nouveau site'}</h3>
              <button onClick={() => setShowSiteForm(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom *</label>
                <input type="text" value={siteForm.nom} onChange={e => setSiteForm({ ...siteForm, nom: e.target.value })} placeholder="Ex: Site 4"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Adresse</label>
                <input type="text" value={siteForm.adresse} onChange={e => setSiteForm({ ...siteForm, adresse: e.target.value })} placeholder="Adresse du site"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ordre d'affichage</label>
                <input type="number" min="0" value={siteForm.ordre_affichage} onChange={e => setSiteForm({ ...siteForm, ordre_affichage: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={siteForm.actif} onChange={e => setSiteForm({ ...siteForm, actif: e.target.checked })} className="rounded text-blue-600" />
                <label className="text-sm text-gray-700 dark:text-gray-300">Actif</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowSiteForm(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Annuler</button>
              <button onClick={handleSaveSite} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Save size={14} /> {editingSite ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL FORM SALLE ═══════════════ */}
      {showSalleForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{editingSalle ? 'Modifier la salle' : 'Nouvelle salle'}</h3>
              <button onClick={() => setShowSalleForm(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom *</label>
                <input type="text" value={salleForm.nom} onChange={e => setSalleForm({ ...salleForm, nom: e.target.value })} placeholder="Ex: Salle 10"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Capacité (places) *</label>
                <input type="number" min="1" value={salleForm.capacite} onChange={e => setSalleForm({ ...salleForm, capacite: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={salleForm.actif} onChange={e => setSalleForm({ ...salleForm, actif: e.target.checked })} className="rounded text-blue-600" />
                <label className="text-sm text-gray-700 dark:text-gray-300">Active</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowSalleForm(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Annuler</button>
              <button onClick={handleSaveSalle} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Save size={14} /> {editingSalle ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL CONFIRMATION SUPPRESSION ═══════════════ */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={24} className="text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Supprimer {deleteConfirm.type === 'site' ? 'ce site' : 'cette salle'} ?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              <span className="font-medium">{deleteConfirm.nom}</span>
              {deleteConfirm.type === 'site' && <span className="block text-red-500 text-xs mt-1">Toutes les salles associées seront aussi supprimées.</span>}
            </p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Annuler</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL DÉTAILS BRASSAGE ═══════════════ */}
      {detailsSessionId !== null && (
        <BrassageDetailsModal
          sessionId={detailsSessionId}
          onClose={() => setDetailsSessionId(null)}
        />
      )}
    </div>
  );
};

export default Brassage;
